<script lang="ts" setup>
import type { BlockPropCustomComponentProps } from '#layers/block-engine/public';
import { onScopeDispose, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useBlockInEditorContext } from '#layers/block-engine/public';
import { useDetailPageContext } from '#layers/blocks/composables/block-context/use-detail-page-context';
import { getCustomizableFormFields } from '#layers/blocks/utils/form-fields';
import { useWorkspaceSchemaStore } from '#layers/data-model/public';
import { useWorkspaceRuntimeHandle } from '#layers/runtime/public';
import { ButtonDestructive, showConfirmationDialog, useToastResultHandling } from '#layers/ui/public';

const { child } = defineProps<BlockPropCustomComponentProps>();

/**
 * Ejected
 */
const model = defineModel<boolean>();

const { t } = useI18n();

const { insertBlock, getSlot } = useBlockInEditorContext();
const schemaStore = useWorkspaceSchemaStore();
const { onError } = useToastResultHandling({ error: { title: t('blocks.form.refresh_fields_failed') } });
const detailPageContext = useDetailPageContext();

const loading = ref(false);

let isMounted = true;

onScopeDispose(() => {
	isMounted = false;
});

/**
 * Append eligible missing fields from freshly loaded schema to the current Form.
 *
 * @param confirmEject Confirm switching from automatic fields before starting the refresh.
 * Completion after unmount, target changes, or workspace replacement does not edit the Form.
 */
async function insertFormFields(confirmEject = false) {
	if (loading.value) return;

	const handle = useWorkspaceRuntimeHandle();
	const childId = child.id;
	const collectionId = child.props.collectionId || detailPageContext.collectionId;
	/** Whether this operation still owns the displayed Form and its collection. */
	const isCurrentTarget = () => isMounted && handle.isActive()
		&& child.id === childId
		&& (child.props.collectionId || detailPageContext.collectionId) === collectionId;

	loading.value = true;

	try {
		if (confirmEject && !await showConfirmationDialog({
			props: {
				title: t('blocks.form.eject_confirm.title'),
				description: t('blocks.form.eject_confirm.description'),
				confirm: {
					label: t('blocks.form.eject_confirm.confirm'),
					color: 'warning',
				},
			},
		})) {
			return;
		}

		if (!isCurrentTarget()) return;
		if (!collectionId) throw new Error('Collection ID is required to insert form fields');

		// Refresh owns manifest, metadata, and runtime collection registration; resolve proxies afterward.
		await schemaStore.refresh();
		if (!isCurrentTarget()) return;

		const collection = schemaStore.getCollection(collectionId);
		if (!collection) throw new Error(`Collection "${collectionId}" not found`);

		const fields = getCustomizableFormFields(collection);

		model.value = true;

		for (const field of fields) {
			if (!isCurrentTarget()) return;
			if (getSlot('default').some((child) => child.props?.fieldId === field.id)) continue;

			await insertBlock({
				block: '$builtin:FormField',
				props: { fieldId: field.id },
			});
		}
	}
	catch (error) {
		if (isCurrentTarget()) onError(error);
	}
	finally {
		loading.value = false;
	}
}

/** Confirm returning to automatic fields. */
async function cancelEject() {
	if (!await showConfirmationDialog({
		props: {
			title: t('blocks.form.cancel_eject_confirm.title'),
			description: t('blocks.form.cancel_eject_confirm.description'),
			confirm: {
				label: t('blocks.form.cancel_eject_confirm.confirm'),
				color: 'error',
			},
		},
	})) {
		return;
	}

	if (!loading.value && isMounted) model.value = false;
}
</script>

<template>
	<DButton
		v-if="loading"
		loading
		block
	/>
	<DButton
		v-else-if="!model"
		:label="$t('blocks.form.eject')"
		icon="lucide:unplug"
		block
		@click="insertFormFields(true)"
	/>
	<div
		v-else
		class="@container"
	>
		<div class="grid grid-cols-1 gap-2 @xs:grid-cols-2">
			<DButton
				:label="$t('blocks.form.refresh_fields')"
				icon="lucide:refresh-cw"
				block
				@click="insertFormFields()"
			/>
			<ButtonDestructive
				:label="$t('blocks.form.cancel_eject')"
				icon="lucide:x"
				variant="outline"
				block
				@click="cancelEject()"
			/>
		</div>
	</div>
</template>
